/**
 * InboxSmartViewChips — horizontal chip strip that filters the inbox by
 * AI-classified smart view (Tutti / Da leggere / Richiede risposta /
 * Con allegato / Stellate / Oggi).
 *
 * Twin of :class:`SmartSourceChips` (documents) — same shape (active
 * pressed state + count badge + glyph) so the two surfaces read as one
 * design language. Italian labels live in :data:`INBOX_SMART_VIEW_LABELS`.
 *
 * The strip is **filter-only** — it does NOT change the grouping mode
 * (thread vs flat) and is composable with the per-channel sub-tab bar
 * (Email / PEC / Slack / …): smart view trims rows, sub-tab partitions
 * by provider, the two compose.
 */

import {
  CheckCircle2,
  Inbox,
  MessageCircle,
  Paperclip,
  Star,
  Sun,
  type LucideIcon,
} from "lucide-react";
import type * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { cn } from "@ando/ui";

import {
  INBOX_SMART_VIEW_LABELS,
  INBOX_SMART_VIEWS,
  type InboxSmartView,
} from "../utils/inboxSmartViews";


/** Visual signature per view — glyph + active tint. Kept in lockstep
 *  with :data:`INBOX_SMART_VIEWS` so adding a new view is a single
 *  entry in both arrays. */
const VIEW_GLYPHS: Readonly<Record<InboxSmartView, LucideIcon>> = {
  tutti: Inbox,
  unread: CheckCircle2,
  needs_reply: MessageCircle,
  attachments: Paperclip,
  starred: Star,
  today: Sun,
};


export interface InboxSmartChipDef {
  readonly id: InboxSmartView;
  readonly badge: number;
}


export interface InboxSmartViewChipsProps {
  readonly activeView: InboxSmartView;
  readonly onChange: (next: InboxSmartView) => void;
  /** Caller-provided badge counts (pre-computed via
   *  :func:`filterByInboxSmartView` once per render). */
  readonly chips: ReadonlyArray<InboxSmartChipDef>;
  readonly className?: string;
}


export const InboxSmartViewChips: React.FC<InboxSmartViewChipsProps> = ({
  activeView,
  onChange,
  chips,
  className,
}) => {
  const { i18n } = useLingui();
  return (
  <div
    role="tablist"
    aria-label={i18n._(msg`Vista intelligente conversazioni`)}
    className={cn(
      "scrollbar-thin flex flex-wrap items-center gap-1.5 overflow-x-auto",
      className,
    )}
  >
    {INBOX_SMART_VIEWS.map((id) => {
      const def = chips.find((c) => c.id === id);
      const Icon = VIEW_GLYPHS[id];
      const active = id === activeView;
      const badge = def?.badge ?? 0;
      return (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={active}
          aria-pressed={active}
          onClick={() => onChange(id)}
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium text-[11.5px] transition-all",
            active
              ? "border-accent/40 bg-accent/[0.08] text-ink-1 shadow-sm"
              : "border-hairline bg-white/55 text-ink-2 hover:border-accent/30 hover:bg-white/75 hover:text-ink-1",
          )}
        >
          <Icon
            size={11}
            strokeWidth={2}
            aria-hidden
            className={cn(active ? "text-ando-orange" : "text-ink-3")}
          />
          {i18n._(INBOX_SMART_VIEW_LABELS[id])}
          {badge > 0 ? (
            <span
              className={cn(
                "ml-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0 font-semibold text-[10px] tabular-nums",
                active
                  ? "bg-accent/15 text-ink-1"
                  : "bg-black/[0.06] text-ink-3",
              )}
            >
              {badge > 99 ? "99+" : badge}
            </span>
          ) : null}
        </button>
      );
    })}
  </div>
  );
};
InboxSmartViewChips.displayName = "InboxSmartViewChips";
